/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useModal } from "../../../context/ModalContext";
import { ThemeContext } from "../../../context/ThemeContext";

import AddressSearchPreview from "../AddressSearchPreview";
import PropertyDetailsSection from "./components/PropertyDetailsSection";
import ManualSheetSection from "./components/ManualSheetSection";
import ResultsModal from "./components/ResultsModal";

import FloatingInput from "../../../components/input/FloatingInput";
import FloatingNumber from "../../../components/input/FloatingNumber";
import CustomSelect from "../../../components/customselect/CustomSelect";
import { CommonHeader } from "../../../components/ui/CommonHeader";
import Toast from "../../../components/common/Toast";

import { useCurrencyFormat } from "./hooks/useCurrencyFormat";
import { useUserTemplate } from "./hooks/useUserTemplate";

import { formatCurrency } from "../../../utils/format";
import { cleanNumber, useToast } from "./sellerNet.shared";
import { useSellerNetForm } from "./hooks/useSellerNetForm";
import { useSellerNetEngine } from "./hooks/useSellerNetEngine";
import { useSellerNetActions } from "./hooks/useSellerNetActions";

import "../../../calculater.css";
import "../../../responsiveness.css";

/**
 * SellerNetSheet.jsx
 * ------------------
 * The UI “shell”:
 * - owns routing + modals
 * - connects engine + actions + template loader to UI components
 * - keeps render tree simple and readable
 */
const SellerNetSheet = () => {
  const [templateApplyKey, setTemplateApplyKey] = useState(0);

  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const theme = useContext(ThemeContext);

  // Toast UI
  const { toast, showToast } = useToast();

  /**
   * STEP: Get user data from localStorage
   * (kept local here to avoid adding another file)
   */
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  // IDs used across the feature
  const mainId = theme?.id || "";
  const userId = user?.id;
  const postalCode = user?.postal_code;
  const mainCompanyName = theme?.main_company_name || null;

  /**
   * STEP: RHF (Form state + arrays + guards)
   */
  const { methods, arrays, refs, replaceArray } = useSellerNetForm();
  const { control, watch, setValue, getValues, reset } = methods;

  // Template + title/escrow use the company tied to the selected property (enriched address),
  // falling back to the logged-in user's company until an address is chosen.
  const addressCompanyId = watch("addressMeta.companyid");
  const companyId =
    (addressCompanyId && String(addressCompanyId).trim()) ||
    user?.companyid ||
    null;

  /**
   * STEP: read route params & query params
   * - calcId means we're editing a saved record
   * - quick mode skips address search
   */
  const params = useParams();
  const calcId = params?.id || params?.calculatorId || params?.calcId;
  const [searchParams] = useSearchParams();
  const isQuickMode = searchParams.get("mode") === "quick";

  /**
   * STEP: Engine hook (all business logic + sync + effects)
   */
  const engine = useSellerNetEngine({
    mainId,
    userId,
    companyId,
    postalCode,
    mainCompanyName,
    calcId,
    isQuickMode,
    user,
    watch,
    getValues,
    setValue,
    reset,
    arrays,
    replaceArray,
    refs,
    showToast,
  });

  /**
   * STEP: Load user template (your existing hook)
   * This populates initial cost arrays when starting a new calc.
   */
  const { templateMissing } = useUserTemplate(mainId, userId, companyId, {
    replaceSellerClosingCost: arrays.sellerClosingCost.replace,
    replaceClosingCostsSection2: arrays.closingCost.replace,
    replaceSellerOtherCost: arrays.sellerOtherCost.replace,
    replaceCompensation: arrays.compensation.replace,
    replaceOtherCost: arrays.otherCost.replace,
    setValue,
    getValues,
    calcId,
    isPopulatingRef: refs.isPopulatingRef,
    applyKey: templateApplyKey,
    showToaster: ({ message, type }) => showToast({ message, type }),
    onAfterTemplateApply: () => engine.applyDerivedCostRows(),
  });
  /**
   * STEP: User actions (Save/Update/PDF)
   */
  const { checkValidSalesPrice, handleSave, handleDownloadPrintPDF } =
    useSellerNetActions({
      user,
      companyId,
      getValues,
      setValue,
      openModal,
      closeModal,
      showToast,
      loadedExistingRef: refs.loadedExistingRef,
      isQuickMode,
      maincompanyid: mainId,
      ttbData: engine.ttbData,
    });

  /**
   * STEP: Currency formatter hook for inputs
   */
  const handleCurrencyBlur = useCurrencyFormat(undefined, undefined, setValue);

  /**
   * STEP: Results modal renderer
   * Keeps modal creation consistent and allows stepping.
   */
  const renderModal = useCallback(
    (step) => (
      <ResultsModal
        step={step}
        setStep={(newStep) =>
          openModal({
            title: "What would you like to do next?",
            size: "smallmodel",
            content: renderModal(newStep),
          })
        }
        closeModal={closeModal}
        onEditManual={() => {
          closeModal();
          engine.setShowManualEditor(true);
        }}
        values={getValues()}
        formatCurrency={formatCurrency}
        cancelModal={() => {
          closeModal();
          openModal({
            title: "Seller's Net Sheet Results",
            size: "halffull",
            content: renderModal(1),
          });
        }}
        cancelResultModal={() => {
          closeModal();
          openModal({
            title: "What would you like to do next?",
            size: "smallmodel",
            content: renderModal(2),
          });
        }}
        saveCalc={handleSave}
        handleDownloadPrintPDF={handleDownloadPrintPDF}
        checkValidSalesPrice={checkValidSalesPrice}
        handleSaveCalc={handleSave}
        getValues={getValues}
        showToaster={({ message, type }) => showToast({ message, type })}
        theme={theme}
      />
    ),
    [
      openModal,
      closeModal,
      getValues,
      handleSave,
      handleDownloadPrintPDF,
      checkValidSalesPrice,
      showToast,
      theme,
      engine,
    ],
  );

  /**
   * STEP: Calculate button handler
   * - If no sales price: show warning modal
   * - Otherwise run engine pipeline then show results
   */
  const handleCalculate = useCallback(async () => {
    // Print the latest RHF values (uncomment for debugging)
    // const allFormValues = getValues();
    //  console.log('🔍 [handleCalculate] Latest RHF form values:', allFormValues);

    const sp = getValues("salesPrice");

    if (!sp || Number(sp) === 0) {
      openModal({
        title: "Warnings",
        size: "smallmodel",
        content: (
          <div className="modelspace pplication-fe">
            <p className="model-desc-details notice-icon">
              You haven't entered a Sales Price.
              <br />
              Do you want to continue without a Sales Price?
            </p>
            <div className="frm-btn popup-2-btn">
              <button
                className="submit-btn btn-bg inpt-btn btn-discard"
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                className="submit-btn btn-bg inpt-btn"
                onClick={async () => {
                  closeModal();
                  await engine.handleSalesPriceBlur();
                  openModal({
                    title: "Seller's Net Sheet Results",
                    size: "halffull",
                    content: renderModal(1),
                  });
                }}
              >
                Continue
              </button>
            </div>
          </div>
        ),
      });
      return;
    }

    await engine.handleSalesPriceBlur();
    openModal({
      title: "Seller's Net Sheet Results",
      size: "halffull",
      content: renderModal(1),
    });
  }, [getValues, openModal, closeModal, engine, renderModal]);

  /**
   * STEP: Start a new calc
   * - resets engine + RHF
   * - reload template
   * - navigates to /seller
   */
  const handleNewCalc = useCallback(() => {
    // engine reset (clears ttbData, flags, etc) + reset RHF form
    engine.handleNewCalcLocal();

    // trigger template re-apply via useEffect inside useUserTemplate
    setTemplateApplyKey((k) => k + 1);

    // remove :id from route if editing
    navigate("/seller", { replace: true });
  }, [engine, navigate]);

  return (
    <div className="calculater-layout">
      <div className="back-inner">
        {!engine.showManualEditor && (
          <CommonHeader
            title="Seller's Net Sheet"
            subTitle="Enter the property address and purchase price to get started."
          />
        )}
      </div>

      {/* containerRef is used by engine to listen blur/change and recalc totals */}
      <div
        ref={engine.containerRef}
        className={`calc-container ${
          engine.showManualEditor ? "calc-page" : "double-height"
        }`}
      >
        <div className="clc-mid-contain">
          {/* Address Search UI */}
          {!engine.showManualEditor && engine.showAddressView && !calcId && (
            <AddressSearchPreview
              onEnriched={engine.handleAddressEnriched}
              onAddressEdited={engine.handleAddressEdited}
              onStreetViewChanged={(camera) =>
                setValue("addressMeta.streetViewOverride", camera)
              }
              control={control}
              mode="seller"
              setValue={setValue}
              openModal={openModal}
              closeModal={closeModal}
              showToaster={({ message, type }) => showToast({ message, type })}
            />
          )}

          <div className="calc-frm-field full-dvd fsapce-top fsapce-bottom">
            {templateMissing && (
              <div
                role="alert"
                style={{
                  color: "#721c24",
                  backgroundColor: "#f8d7da",
                  border: "1px solid #f5c6cb",
                  padding: "8px",
                  borderRadius: "4px",
                  marginTop: "8px",
                }}
              >
                Template not found
              </div>
            )}
          </div>

          {/* Property Details UI */}
          {engine.showPreviewFromChild &&
            !engine.showManualEditor &&
            !calcId &&
            !engine.templateMissing && (
              <PropertyDetailsSection
                ttbData={engine.ttbData}
                formatCurrency={formatCurrency}
                salesPriceError=""
                mortgageAmountError=""
                isCorrectProperty={engine.isCorrectProperty}
                isInitialLoad={engine.isInitialLoad}
                setIsCorrectProperty={engine.setIsCorrectProperty}
                setIsInitialLoad={engine.setIsInitialLoad}
                useAnnualPropertyTax={engine.useAnnualPropertyTax}
                setUseAnnualPropertyTax={engine.setUseAnnualPropertyTax}
                useMortgageBalance={engine.useMortgageBalance}
                setUseMortgageBalance={engine.setUseMortgageBalance}
                useHomeWarranty={engine.useHomeWarranty}
                setUseHomeWarranty={engine.setUseHomeWarranty}
                getValues={getValues}
                setValue={setValue}
                handleCurrencyBlur={handleCurrencyBlur}
                onSalesPriceBlur={engine.handleSalesPriceBlur}
                control={control}
                cleanNumber={cleanNumber}
                watch={watch}
                handleCalculate={handleCalculate}
              />
            )}
        </div>

        {/* Manual sheet UI */}
        {engine.showManualEditor && (
          <ManualSheetSection
            mainId={mainId}
            FloatingInput={FloatingInput}
            FloatingNumber={FloatingNumber}
            CustomSelect={CustomSelect}
            formatCurrency={formatCurrency}
            salesPriceError=""
            mortgageAmountError=""
            navigate={navigate}
            isCorrectProperty={engine.isCorrectProperty}
            useAnnualPropertyTax={engine.useAnnualPropertyTax}
            setUseAnnualPropertyTax={engine.setUseAnnualPropertyTax}
            useMortgageBalance={engine.useMortgageBalance}
            setUseMortgageBalance={engine.setUseMortgageBalance}
            handleCurrencyBlur={handleCurrencyBlur}
            onSalesPriceBlur={engine.handleSalesPriceBlur}
            checkValidSalesPrice={checkValidSalesPrice}
            handleDownloadPrintPDF={handleDownloadPrintPDF}
            control={control}
            watch={watch}
            setValue={setValue}
            getValues={getValues}
            sellerClosingCostArray={arrays.sellerClosingCost}
            closingCostsSection2Array={arrays.closingCost}
            sellerOtherCostArray={arrays.sellerOtherCost}
            compensationArray={arrays.compensation}
            otherCostArray={arrays.otherCost}
            onClose={handleNewCalc}
            cleanNumber={cleanNumber}
            handleSellerCalculation={engine.calculateTotals}
            ttbData={engine.ttbData}
            handleSaveCalc={handleSave}
            showToaster={({ message, type }) => showToast({ message, type })}
            setCompanyId={() => {}}
            templateMissing={engine.templateMissing}
            isQuickMode={isQuickMode}
            onAddressEnriched={engine.handleAddressEnriched}
            onZipVerified={engine.handleManualZipVerified}
          />
        )}
      </div>

      {/* Global toast display */}
      <Toast
        message={toast.message}
        type={toast.type}
        visible={toast.visible}
      />
    </div>
  );
};

export default SellerNetSheet;
